import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import Select from "react-select";
import {
    Loader2, User, Mail, Lock, Eye, EyeOff, UserCheck,
    AlertCircle, CheckCircle2, ShieldCheck, FileText, Phone, Globe
} from "lucide-react";

import {
    activateInvite,
    getInviteInfo,
    acceptInvite
} from "@/api/auth";
import { getCountries } from "@/api/kuizy";
import TermsCheckbox from "@/widgets/content/TermsWidget";
import PdpaCheckbox from "@/widgets/content/PdpaWidget";

export default function InviteActivatePage() {
    const nav = useNavigate();
    const { token } = useParams();
    const isFirst = !!token;
    const [searchParams] = useSearchParams();
    const jwtToken = searchParams.get('d');

    const decoded = JSON.parse(atob(jwtToken));

    const [form, setForm] = useState({
        username: decoded.username ?? "",
        email: decoded.email ?? "",
        first_name: decoded.first_name ?? "",
        last_name: decoded.last_name ?? "",
        phone_code: decoded.phone_code ?? "+66",
        phone: decoded.phone ?? "",
        country: decoded.country ?? "TH",
        pass: "",
        pass2: "",
        acceptTerms: false,
        acceptPdpa: false,
    });

    const [countries, setCountries] = useState([]);
    const [inviteInfo, setInviteInfo] = useState(null);
    const [err, setErr] = useState("");
    const [ok, setOk] = useState("");
    const [loadingInvite, setLoadingInvite] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [show1, setShow1] = useState(false);
    const [show2, setShow2] = useState(false);

    /* ---------- load data ---------- */
    useEffect(() => {
        getCountries()
            .then(r => setCountries(r?.data || []))
            .catch(() => setCountries([]));
    }, []);

    useEffect(() => {
        console.log(decoded);
        getInviteInfo(token, jwtToken)
            .then(({ data }) => {
                setInviteInfo(data);
                setForm(f => ({
                    ...f,
                    email: data?.email ?? decoded.email ?? "",
                    username: data?.username ?? decoded.username ?? "",
                    first_name: data?.first_name ?? decoded.first_name ?? "",
                    last_name: data?.last_name ?? decoded.last_name ?? "",
                    phone_code: data?.phone_code ?? decoded.phone_code ?? "+66",
                    phone: data?.phone ?? decoded.phone ?? "",
                    country: data?.country ?? decoded.country ?? "TH",
                }));
                setOk("โปรดกรอกข้อมูลและตั้งรหัสผ่าน");
            })
            .catch(() => setErr("ลิงก์ไม่ถูกต้องหรือหมดอายุ"))
            .finally(() => setLoadingInvite(false));
    }, [token]);

    const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const passRx = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/
    const usernameRx = /^[A-Za-z0-9]+$/
    const countryRx = /^[A-Za-z]{2}$/

    const passScore = useMemo(() => {
        const p = form.pass || ''
        let s = 0
        if (p.length >= 8) s++
        if (/[0-9]/.test(p)) s++
        if (/[A-Z]/.test(p)) s++
        if (/[^A-Za-z0-9]/.test(p)) s++
        return s
    }, [form.pass])
    const passLabel = passScore >= 4 ? 'แข็งแรงมาก' : passScore === 3 ? 'แข็งแรง' : passScore === 2 ? 'ปานกลาง' : form.pass ? 'อ่อน' : ''
    const field = 'w-full pl-11 pr-4 border border-gray-300 rounded-xl py-3 text-base focus:ring-2 focus:ring-primary focus:outline-none placeholder-gray-400 bg-white/70 disabled:bg-gray-100'
    const pill = cls => `inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${cls}`
    const selectStyle = { control: b => ({ ...b, minHeight: 48, borderRadius: 12 }) }

    /* ---------- submit ---------- */
    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;

        setErr("");
        setOk("");
        setSubmitting(true);

        try {
            // ---------- CASE: user exists ----------
            if (inviteInfo?.user_exists) {
                try {
                    await acceptInvite(token, {
                        accepted_at: new Date().toISOString(),
                    });

                    setOk("ยืนยันคำเชิญสำเร็จ กำลังนำคุณไปยังหน้าเข้าสู่ระบบ…");
                    nav("/login", { replace: true });
                    return;

                } catch (err) {
                    const st = err?.response?.status;
                    if (st === 429) setErr("พยายามยืนยันบ่อยเกินไป กรุณาลองใหม่ภายหลัง");
                    else if (st >= 500) setErr("เซิร์ฟเวอร์มีปัญหา กรุณาลองใหม่ภายหลัง");
                    else setErr("ไม่สามารถยืนยันคำเชิญได้");
                    return;
                }
            }

            // ---------- VALIDATION ----------
            if (!form.username.trim() || !usernameRx.test(form.username.trim())) {
                setErr("ชื่อผู้ใช้ต้องเป็นภาษาอังกฤษและตัวเลขเท่านั้น");
                return;
            }
            if (form.email && !emailRx.test(form.email.trim())) {
                setErr("อีเมลไม่ถูกต้อง");
                return;
            }
            if (!form.first_name.trim() || !form.last_name.trim()) {
                setErr("กรุณากรอกชื่อและนามสกุล");
                return;
            }
            if (!form.phone_code.trim()) {
                setErr("กรุณากรอกรหัสประเทศโทรศัพท์");
                return;
            }
            if (!form.phone.trim()) {
                setErr("กรุณากรอกเบอร์โทรศัพท์");
                return;
            }
            if (!countryRx.test((form.country || "").trim())) {
                setErr("รหัสประเทศต้องเป็นตัวอักษร 2 ตัว เช่น TH, US");
                return;
            }
            if (!passRx.test(form.pass)) {
                setErr("รหัสผ่านต้องยาวอย่างน้อย 8 ตัว และมีทั้งตัวอักษรและตัวเลข");
                return;
            }
            if (form.pass !== form.pass2) {
                setErr("รหัสผ่านยืนยันไม่ตรงกัน");
                return;
            }
            if (!form.acceptTerms || !form.acceptPdpa) {
                setErr("กรุณายอมรับข้อกำหนดและนโยบายความเป็นส่วนบุคคล");
                return;
            }

            // ---------- ACTIVATE INVITE ----------
            await activateInvite(token, {
                username: form.username.trim(),
                password: form.pass,
                email: form.email || undefined,
                first_name: form.first_name,
                last_name: form.last_name,
                phone_code: form.phone_code,
                phone: form.phone,
                country: form.country,
                accept_terms: true,
                accept_pdpa: true,
                accepted_at: new Date().toISOString(),
                terms_version: "2025-09-17",
                privacy_version: "2025-09-17",
            });

            setOk("ยืนยันบัญชีสำเร็จ กำลังนำคุณไปยังหน้าเข้าสู่ระบบ…");
            nav("/login", { replace: true });

        } catch (err) {
            setErr("ไม่สามารถยืนยันคำเชิญได้");
        } finally {
            setSubmitting(false);
        }
    };


    const countryValue = form.country ? { value: form.country, label: countries.find(c => c.iso2 === form.country)?.name_en || form.country } : null
    const countryOptions = countries.map(c => ({ value: c.iso2, label: `${c.name_en} (${c.phone_code})` }))

    /* ---------- UI ---------- */
    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-tr from-primary via-info to-secondary p-4">
            <div className="w-full max-w-xl bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl px-8 py-8 flex flex-col gap-6">
                <div className="text-center space-y-2">
                    <h1 className="text-4xl font-extrabold text-primary tracking-wide">
                        <img src="/images/app-logo.png" alt="Kuizy" className="h-20 mx-auto opacity-95" />
                    </h1>
                    <h2 className="text-2xl font-extrabold text-gray-900">{inviteInfo?.user_exists ? 'ยืนยันคำเชิญ' : 'ยืนยันคำเชิญและตั้งรหัสผ่าน'}</h2>
                    <p className="text-gray-500 text-sm">
                        {inviteInfo?.user_exists ? 'กรุณาตรวจสอบข้อมูลด้านล่าง แล้วกดยืนยันเพื่อยอมรับคำเชิญเข้าสู่ระบบขององค์กร' : 'กรอกข้อมูลให้ครบและตั้งรหัสผ่านเพื่อเปิดใช้งานบัญชี จากนั้นจะพาไปหน้าเข้าสู่ระบบ'}
                    </p>
                </div>

                {!!err && (
                    <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                        <AlertCircle className="h-5 w-5 mt-0.5" />
                        <div className="text-sm">{err}</div>
                    </div>
                )}

                {!err && !!ok && (
                    <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700">
                        <CheckCircle2 className="h-5 w-5 mt-0.5" />
                        <div className="text-sm">{ok}</div>
                    </div>
                )}

                <form onSubmit={submit} className="flex flex-col gap-4">
                    {inviteInfo?.user_exists ? (
                        <>
                            <div className="text-center gap-4">
                                <img
                                    src={inviteInfo?.org_logo || '/images/default-org.png'}
                                    alt={inviteInfo?.org_name || 'org'}
                                    className="h-32 w-32 rounded-md object-cover mx-auto border border-gray-200 shadow-md"
                                />
                                <div className="flex-1">
                                    <div className="text-sm text-gray-500">องค์กร</div>
                                    <div className="font-medium">{inviteInfo.org?.name || inviteInfo.organization || inviteInfo.org_name || '-'}</div>
                                </div>
                            </div>

                            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="border border-gray-200 rounded-xl p-4 grid gap-2 md:grid-cols-3 text-sm bg-white">
                                <div className="flex items-center gap-2 text-gray-500"><Mail className="h-4 w-4 text-primary" />อีเมล</div>
                                <div className="md:col-span-2 break-all">{inviteInfo.email || '-'}</div>
                                <div className="flex items-center gap-2 text-gray-500"><User className="h-4 w-4 text-primary" />ชื่อ–สกุล</div>
                                <div className="md:col-span-2">{`${form.first_name || ''} ${form.last_name || ''}`.trim() || '-'}</div>
                                <div className="flex items-center gap-2 text-gray-500"><Phone className="h-4 w-4 text-primary" />โทรศัพท์</div>
                                <div className="md:col-span-2">{`${form.phone_code || ''} ${form.phone || ''}`.trim() || '-'}</div>
                                <div className="flex items-center gap-2 text-gray-500"><Globe className="h-4 w-4 text-primary" />ประเทศ</div>
                                <div className="md:col-span-2">{countries.find(c => c.iso2 === form.country)?.name_en || form.country || '-'}</div>
                            </motion.div>


                            <button
                                className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 disabled:opacity-50 transition text-white font-semibold rounded-xl py-3 flex justify-center items-center gap-2 shadow-lg"
                                disabled={submitting || loadingInvite}
                            >
                                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <UserCheck className="h-5 w-5" />}
                                {submitting ? 'กำลังยืนยัน…' : 'ยืนยันคำเชิญ'}
                            </button>
                        </>
                    ) : (
                        <>
                            {inviteInfo && (
                                <>
                                    <div className="grid md:grid-cols-2 gap-3">
                                        <div className="relative">
                                            <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <input
                                                placeholder="ชื่อจริง"
                                                value={form.first_name}
                                                onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                                                className={field}
                                            />
                                        </div>
                                        <div className="relative">
                                            <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <input
                                                placeholder="นามสกุล"
                                                value={form.last_name}
                                                onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                                                className={field}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid md:grid-cols-3 gap-3">
                                        <div className="relative">
                                            <Globe className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <Select
                                                classNamePrefix="rs"
                                                styles={selectStyle}
                                                options={countryOptions}
                                                value={countryValue}
                                                onChange={o => {
                                                    const c = countries.find(x => x.iso2 === (o?.value || ''))
                                                    setForm(f => ({ ...f, country: o?.value || 'TH', phone_code: c?.phone_code || f.phone_code }))
                                                }}
                                                placeholder="ประเทศ"
                                                isClearable
                                            />
                                        </div>
                                        <div className="relative">
                                            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <input
                                                placeholder="+66"
                                                value={form.phone_code}
                                                onChange={e => setForm(f => ({ ...f, phone_code: e.target.value }))}
                                                className={field}
                                            />
                                        </div>
                                        <div className="relative">
                                            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <input
                                                placeholder="เบอร์โทรศัพท์"
                                                value={form.phone}
                                                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                                                className={field}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="relative">
                                <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <input
                                    placeholder="บัญชีผู้ใช้"
                                    value={form.username}
                                    onChange={e => {
                                        const v = e.target.value;
                                        setForm(f => ({ ...f, username: v }))
                                    }}
                                    className={field}
                                />
                            </div>

                            <div className="space-y-2">
                                <div className="relative">
                                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        type={show1 ? 'text' : 'password'}
                                        placeholder={isFirst ? 'ตั้งรหัสผ่านใหม่' : 'รหัสผ่าน'}
                                        autoComplete={isFirst ? 'new-password' : 'current-password'}
                                        value={form.pass}
                                        onChange={e => setForm(f => ({ ...f, pass: e.target.value }))}
                                        className={field}
                                    />
                                    <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => setShow1(v => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                                        {show1 ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                    </button>
                                </div>

                                {inviteInfo && (
                                    <>
                                        <div className="flex items-center gap-2">
                                            {passLabel && <span className={pill(passScore >= 3 ? 'bg-emerald-100 text-emerald-700' : passScore === 2 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700')}>ความแข็งแรง: {passLabel}</span>}
                                            <span className="text-xs text-gray-500">อย่างน้อย 8 ตัว มีตัวอักษรและตัวเลข</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div className={`flex items-center gap-2 rounded-lg px-2 py-1 ${form.pass.length >= 8 ? 'text-emerald-700 bg-emerald-50' : 'text-gray-500 bg-gray-50'}`}><CheckCircle2 className="h-4 w-4" />8 ตัวอักษรขึ้นไป</div>
                                            <div className={`flex items-center gap-2 rounded-lg px-2 py-1 ${/[0-9]/.test(form.pass) ? 'text-emerald-700 bg-emerald-50' : 'text-gray-500 bg-gray-50'}`}><CheckCircle2 className="h-4 w-4" />มีตัวเลขอย่างน้อย 1 ตัว</div>
                                            <div className={`flex items-center gap-2 rounded-lg px-2 py-1 ${/[A-Za-z]/.test(form.pass) ? 'text-emerald-700 bg-emerald-50' : 'text-gray-500 bg-gray-50'}`}><CheckCircle2 className="h-4 w-4" />มีตัวอักษร</div>
                                            <div className={`flex items-center gap-2 rounded-lg px-2 py-1 ${/[^A-Za-z0-9]/.test(form.pass) ? 'text-emerald-700 bg-emerald-50' : 'text-gray-500 bg-gray-50'}`}><CheckCircle2 className="h-4 w-4" />อักขระพิเศษ (แนะนำ)</div>
                                        </div>
                                    </>
                                )}
                            </div>

                            {inviteInfo && (
                                <div className="relative">
                                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        type={show2 ? 'text' : 'password'}
                                        placeholder="ยืนยันรหัสผ่าน"
                                        autoComplete="new-password"
                                        value={form.pass2}
                                        onChange={e => setForm(f => ({ ...f, pass2: e.target.value }))}
                                        className={field}
                                    />
                                    <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => setShow2(v => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                                        {show2 ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                    </button>
                                </div>
                            )}

                            {inviteInfo && (
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <FileText className="h-4 w-4 text-primary" />
                                        <span>โปรดอ่านและยอมรับเพื่อเปิดใช้บัญชี</span>
                                    </div>
                                    <TermsCheckbox checked={form.acceptTerms} onChange={v => setForm(f => ({ ...f, acceptTerms: v }))} />
                                    <PdpaCheckbox checked={form.acceptPdpa} onChange={v => setForm(f => ({ ...f, acceptPdpa: v }))} />
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                        <ShieldCheck className="h-4 w-4" />
                                        สามารถถอนความยินยอมด้านการตลาดหรือโปรไฟล์ได้ภายหลังในหน้าการตั้งค่า
                                    </div>
                                </div>
                            )}

                            <button
                                className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 disabled:opacity-50 transition text-white font-semibold rounded-xl py-3 flex justify-center items-center gap-2 shadow-lg"
                                disabled={submitting || loadingInvite || (inviteInfo && (!form.acceptTerms || !form.acceptPdpa))}
                            >
                                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : inviteInfo?.user_exists ? <UserCheck className="h-5 w-5" /> : <User className="h-5 w-5" />}
                                {submitting ? (inviteInfo?.user_exists ? 'กำลังยืนยัน…' : 'กำลังเข้าสู่ระบบ…') : (inviteInfo?.user_exists ? 'ยืนยันและไปหน้าเข้าสู่ระบบ' : 'เข้าสู่ระบบ')}
                            </button>
                        </>
                    )}
                </form>


            </div>
        </div>
    );
}
